import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { PractitionerPhoto } from '@/types/practitioner-photos';

interface PractitionerPhotoGalleryProps {
  photos: PractitionerPhoto[];
}

export default function PractitionerPhotoGallery({ photos }: PractitionerPhotoGalleryProps) {
  const { t } = useTranslation('practitioners');
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  
  // Sort photos by display order
  const sortedPhotos = [...photos].sort((a, b) => a.display_order - b.display_order);

  const openLightbox = (index: number) => {
    setSelectedPhotoIndex(index);
  };

  const closeLightbox = () => {
    setSelectedPhotoIndex(null);
  };

  const goToPrevious = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex < sortedPhotos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };

  if (!sortedPhotos || sortedPhotos.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-foreground">{t('gallery')}</h3>
      
      {/* Photo Gallery */}
      <div className="flex gap-3 overflow-x-auto pb-2">
        {sortedPhotos.map((photo, index) => (
          <div
            key={photo.id}
            className="flex-shrink-0 cursor-pointer group"
            onClick={() => openLightbox(index)}
          >
            <div className="w-32 h-24 sm:w-40 sm:h-30 rounded-lg overflow-hidden border border-border hover:border-primary transition-colors">
              <img
                src={photo.photo_url}
                alt={photo.photo_description || `Gallery image ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                loading="lazy"
              />
            </div>
            {photo.photo_description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2 w-32 sm:w-40">
                {photo.photo_description}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={selectedPhotoIndex !== null} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          {selectedPhotoIndex !== null && (
            <div className="relative bg-background">
              {/* Close button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-background/80 hover:bg-background transition-colors"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Navigation buttons */}
              {sortedPhotos.length > 1 && (
                <>
                  <button
                    onClick={goToPrevious}
                    disabled={selectedPhotoIndex === 0}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-background/80 hover:bg-background transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={goToNext}
                    disabled={selectedPhotoIndex === sortedPhotos.length - 1}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-background/80 hover:bg-background transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Main image */}
              <div className="flex items-center justify-center min-h-[60vh] p-4">
                <img
                  src={sortedPhotos[selectedPhotoIndex].photo_url}
                  alt={sortedPhotos[selectedPhotoIndex].photo_description || `Gallery image ${selectedPhotoIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {/* Image description */}
              {sortedPhotos[selectedPhotoIndex].photo_description && (
                <div className="p-4 border-t border-border bg-muted/30">
                  <p className="text-sm text-foreground text-center">
                    {sortedPhotos[selectedPhotoIndex].photo_description}
                  </p>
                </div>
              )}

              {/* Photo counter */}
              {sortedPhotos.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-background/80 rounded-full text-xs text-muted-foreground">
                  {selectedPhotoIndex + 1} / {sortedPhotos.length}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}